import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NumPrevHeatmapImage } from 'services/api/Meteorology';
import { HEATMAP_MODELS, HEATMAP_VARIABLES } from 'helper/products/meteorology/heatmapModels';
import { MeteorologyViewMode } from './meteorologyFieldsObservedDataControlPanelReducer';

export interface NumericForecastConsultSnapshot {
  viewMode: MeteorologyViewMode;
  variable?: string;
  models: string[];
  region?: string;
  period?: string;
  forecastUnixDate?: number;
  anomaly: boolean;
}

interface MeteorologyFieldsControlPanelState {
  region: string | undefined
  models: string[] | undefined
  forecastUnixDate: number | undefined
  period: string | undefined
  selectedOffset: number,
  anomaly: boolean,
  variable: string | undefined,
  used: boolean,
  viewMode: MeteorologyViewMode,
  heatmapImages: NumPrevHeatmapImage[],
  consultRequestId: number,
  lastConsulted?: NumericForecastConsultSnapshot,
}

const initialState: MeteorologyFieldsControlPanelState = {
  region: undefined,
  models: [],
  forecastUnixDate: undefined,
  period: undefined,
  selectedOffset: 0,
  anomaly: false,
  variable: undefined,
  used: false,
  viewMode: 'spatial',
  heatmapImages: [] as NumPrevHeatmapImage[],
  consultRequestId: 0,
  lastConsulted: undefined,
};

export const meteorologyFieldsControlPanelSlice = createSlice({
  name: 'meteorologyFieldsControlPanel',
  initialState,
  reducers: {
    setRegion: (state, action: PayloadAction<string>) => {
      state.region = action.payload;
    },
    setForecastUnixDate: (state, action: PayloadAction<number>) => {
      state.forecastUnixDate = action.payload;
    },
    setPeriod: (state, action: PayloadAction<string>) => {
      state.period = action.payload;
    },
    setModels: (state, action: PayloadAction<string[]>) => {
      state.models = action.payload;
    },
    setDateOffset: (state, action: PayloadAction<number>) => {
      state.selectedOffset = action.payload;
    },
    setAnomaly: (state, action: PayloadAction<boolean>) => {
      state.anomaly = action.payload;
    },
    setVariable: (state, action: PayloadAction<string>) => {
      state.variable = action.payload;
      if (state.viewMode === 'heatmap'
          && !(HEATMAP_VARIABLES as readonly string[]).includes(action.payload)) {
        state.viewMode = 'spatial';
      }
    },
    setUsed: (state, action: PayloadAction<boolean>) => {
      state.used = action.payload;
    },
    setViewMode: (state, action: PayloadAction<MeteorologyViewMode>) => {
      state.viewMode = action.payload;
      // Ao trocar para Heatmap, limpa variavel e modelos que nao sao suportados.
      // Espelha as constantes HEATMAP_VARIABLES e HEATMAP_MODELS do backend
      // (exclusivo/modules/mod_produtos/controller/previsao_numerica_tempo.py).
      if (action.payload === 'heatmap') {
        if (state.variable && !(HEATMAP_VARIABLES as readonly string[]).includes(state.variable)) {
          state.variable = undefined;
        }
        if (state.models && state.models.length > 0) {
          state.models = state.models.filter(
            (m) => (HEATMAP_MODELS as readonly string[]).includes(m),
          );
        }
      }
    },
    setHeatmapImages: (state, action: PayloadAction<NumPrevHeatmapImage[]>) => {
      state.heatmapImages = action.payload;
    },
    requestConsult: (state) => {
      state.consultRequestId += 1;
    },
    setLastConsulted: (state, action: PayloadAction<NumericForecastConsultSnapshot>) => {
      state.lastConsulted = action.payload;
    },
    reset: (state) => {
      state.forecastUnixDate = undefined;
      state.region = undefined;
      state.models = [];
      state.period = undefined;
      state.selectedOffset = 0;
      state.anomaly = false;
      state.variable = undefined;
      state.used = false;
      state.viewMode = 'spatial';
      state.heatmapImages = [];
      // consultRequestId NÃO é zerado: é um contador monotônico de disparo.
      // Zerá-lo causaria colisão (0→1) ao disparar requestConsult logo após reset.
      state.lastConsulted = undefined;
    },
  },
});

/* eslint-disable @typescript-eslint/no-explicit-any */
export const selectMFControlPanelForecastUnixDate = (state: any) => (
  state.meteorologyFieldsControlPanel.forecastUnixDate
);
export const selectMFControlPanelPeriod = (state: any) => (
  state.meteorologyFieldsControlPanel.period
);
export const selectMFControlPanelModels = (state: any) => (
  state.meteorologyFieldsControlPanel.models
);
export const selectMFControlPanelRegion = (state: any) => (
  state.meteorologyFieldsControlPanel.region
);
export const selectMFControlPanelDateOffset = (state: any) => (
  state.meteorologyFieldsControlPanel.selectedOffset
);
export const selectMFControlPanelAnomaly = (state: any) => (
  state.meteorologyFieldsControlPanel.anomaly
);
export const selectMFControlPanelVariable = (state: any) => (
  state.meteorologyFieldsControlPanel.variable
);
export const selectMFControlPanelUsed = (state: any) => (
  state.meteorologyFieldsControlPanel.used
);
export const selectMFControlPanelViewMode = (state: any): MeteorologyViewMode => (
  state.meteorologyFieldsControlPanel.viewMode
);
export const selectMFControlPanelHeatmapImages = (state: any): NumPrevHeatmapImage[] => (
  state.meteorologyFieldsControlPanel.heatmapImages
);
export const selectMFControlPanelConsultRequestId = (state: any): number => (
  state.meteorologyFieldsControlPanel.consultRequestId
);
export const selectMFControlPanelLastConsulted = (
  state: any,
): NumericForecastConsultSnapshot | undefined => (
  state.meteorologyFieldsControlPanel.lastConsulted
);
/* eslint-enable @typescript-eslint/no-explicit-any */
export const {
  setPeriod,
  setModels,
  setRegion,
  setDateOffset,
  setForecastUnixDate,
  setAnomaly,
  setVariable,
  setUsed,
  setViewMode,
  setHeatmapImages,
  requestConsult,
  setLastConsulted,
  reset,
} = meteorologyFieldsControlPanelSlice.actions;

export default meteorologyFieldsControlPanelSlice.reducer;
